/**
 * MT Classes Component JavaScript
 * Handles sport type switching, class selection, and video playback
 */

class MTClasses {
  constructor(wrapper) {
    this.wrapper = wrapper;
    this.tabs = wrapper.querySelectorAll('.mt-classes-tab');
    this.classLists = wrapper.querySelectorAll('.mt-classes-list');
    this.videoContainer = wrapper.querySelector('#videoPlayer');
    this.navPrev = wrapper.querySelector('.mt-classes-nav-prev');
    this.navNext = wrapper.querySelector('.mt-classes-nav-next');
    this.tabsContainer = wrapper.querySelector('.mt-classes-tabs-container');
    
    // Mobile modal elements
    this.videoModal = wrapper.querySelector('#videoModal');
    this.videoModalPlayer = wrapper.querySelector('#videoModalPlayer');
    this.videoModalClose = wrapper.querySelector('#videoModalClose');
    this.videoModalOverlay = wrapper.querySelector('.mt-classes-video-modal-overlay');
    
    // Check if mobile
    this.isMobile = window.innerWidth <= 1024;
    
    this.currentSportIndex = 0;
    this.currentClassIndex = 0;
    this.hasPreparedInitialVideo = false;
    this.hasLoadedInitialVideo = false;
    this.inViewObserver = null;
    
    this.init();
    
    // Update mobile detection on resize
    window.addEventListener('resize', () => {
      this.isMobile = window.innerWidth <= 1024;
    });
  }

  init() {
    // Initialize event listeners
    this.setupTabListeners();
    this.setupClassListeners();
    this.setupNavigationButtons();
    this.setupModalListeners();
    
    // Desktop video loads only when the component approaches the viewport.
    if (!this.isMobile) {
      this.prepareInitialVideo();
      this.setupViewportLoading();
    }
    
    // Update navigation button states
    this.updateNavButtons();
  }

  setupTabListeners() {
    this.tabs.forEach((tab, index) => {
      tab.addEventListener('click', () => {
        this.switchSport(index);
      });
    });
  }

  setupClassListeners() {
    this.classLists.forEach((list) => {
      const classItems = list.querySelectorAll('.mt-class-item');
      classItems.forEach((item, index) => {
        item.addEventListener('click', () => {
          this.selectClass(list, item, index);
        });
      });
    });
  }

  setupNavigationButtons() {
    if (this.navPrev) {
      this.navPrev.addEventListener('click', () => {
        this.scrollTabs('prev');
      });
    }

    if (this.navNext) {
      this.navNext.addEventListener('click', () => {
        this.scrollTabs('next');
      });
    }

    // Update button states on scroll
    if (this.tabsContainer) {
      this.tabsContainer.addEventListener('scroll', () => {
        this.updateNavButtons();
      });
    }
  }

  setupModalListeners() {
    // Close button
    if (this.videoModalClose) {
      this.videoModalClose.addEventListener('click', () => {
        this.closeVideoModal();
      });
    }

    // Close on overlay click
    if (this.videoModalOverlay) {
      this.videoModalOverlay.addEventListener('click', () => {
        this.closeVideoModal();
      });
    }

    // Close on ESC key
    document.addEventListener('keydown', (e) => {
      if (e.key === 'Escape' && this.videoModal && this.videoModal.classList.contains('active')) {
        this.closeVideoModal();
      }
    });
  }

  switchSport(index) {
    // Update active tab
    this.tabs.forEach((tab, i) => {
      if (i === index) {
        tab.classList.add('active');
      } else {
        tab.classList.remove('active');
      }
    });

    // Update active class list
    this.classLists.forEach((list, i) => {
      if (i === index) {
        list.classList.add('active');
        
        // Load first video of the selected sport (but don't auto-play)
        // On mobile, just mark as active, don't load video
        const firstClass = list.querySelector('.mt-class-item');
        if (firstClass) {
          if (this.isMobile) {
            // On mobile, just mark as active, don't auto-load
            const allClassItems = list.querySelectorAll('.mt-class-item');
            allClassItems.forEach((item) => {
              item.classList.remove('active');
            });
            firstClass.classList.add('active');
            this.currentClassIndex = 0;
          } else {
            if (!this.hasLoadedInitialVideo) {
              this.markClassAsActive(list, firstClass, 0);
              this.preparePreviewForClass(firstClass);
            } else {
              this.selectClass(list, firstClass, 0);
            }
          }
        }
      } else {
        list.classList.remove('active');
      }
    });

    this.currentSportIndex = index;
  }

  selectClass(list, classItem, index) {
    this.markClassAsActive(list, classItem, index);

    // Load video
    const videoUrl = classItem.dataset.videoUrl;
    const posterUrl = this.getPosterUrl(classItem);
    
    if (videoUrl) {
      if (this.isMobile) {
        // On mobile, load video in modal
        this.loadVideoInModal(videoUrl, posterUrl);
      } else {
        // On desktop, load video in right column
        this.hasLoadedInitialVideo = true;
        this.disconnectViewportObservers();
        this.loadVideo(videoUrl, posterUrl);
      }
    }

    this.currentClassIndex = index;
  }

  markClassAsActive(list, classItem, index) {
    const allClassItems = list.querySelectorAll('.mt-class-item');
    allClassItems.forEach((item) => {
      item.classList.remove('active');
    });

    classItem.classList.add('active');
    this.currentClassIndex = index;
  }

  getPosterUrl(classItem) {
    if (!classItem) return null;

    if (classItem.dataset.posterUrl) {
      return classItem.dataset.posterUrl;
    }

    const thumbnailImg = classItem.querySelector('.class-thumbnail img');
    return thumbnailImg ? thumbnailImg.currentSrc || thumbnailImg.src : null;
  }

  getInitialClassTarget() {
    const activeList = this.classLists[this.currentSportIndex] || this.wrapper.querySelector('.mt-classes-list.active') || this.classLists[0];
    if (!activeList) return null;

    const activeClass = activeList.querySelector('.mt-class-item.active');
    if (activeClass) {
      const activeIndex = Array.from(activeList.querySelectorAll('.mt-class-item')).indexOf(activeClass);
      return { list: activeList, classItem: activeClass, index: activeIndex >= 0 ? activeIndex : 0 };
    }

    const firstClass = activeList.querySelector('.mt-class-item');
    if (!firstClass) return null;

    return { list: activeList, classItem: firstClass, index: 0 };
  }

  setupViewportLoading() {
    if (!this.videoContainer) return;

    if (!('IntersectionObserver' in window)) {
      this.prepareInitialVideo();
      this.loadInitialVideo();
      return;
    }

    this.inViewObserver = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (!entry.isIntersecting) return;
          this.loadInitialVideo();
          this.inViewObserver.disconnect();
          this.inViewObserver = null;
        });
      },
      {
        rootMargin: '0px',
        threshold: 0.2
      }
    );

    this.inViewObserver.observe(this.wrapper);
  }

  disconnectViewportObservers() {
    if (this.inViewObserver) {
      this.inViewObserver.disconnect();
      this.inViewObserver = null;
    }
  }

  prepareInitialVideo() {
    if (this.isMobile || this.hasPreparedInitialVideo) return;

    const initialTarget = this.getInitialClassTarget();
    if (!initialTarget) return;

    this.hasPreparedInitialVideo = true;
    this.preparePreviewForClass(initialTarget.classItem);
  }

  preparePreviewForClass(classItem) {
    if (!this.videoContainer || !classItem || this.hasLoadedInitialVideo) return;

    const posterUrl = this.getPosterUrl(classItem);
    if (!posterUrl) return;

    this.videoContainer.innerHTML = '';

    const previewImage = document.createElement('img');
    previewImage.src = posterUrl;
    previewImage.alt = classItem.querySelector('.class-title')?.textContent?.trim() || 'Class preview';
    previewImage.className = 'mt-classes-video-poster-preview';
    previewImage.loading = 'eager';
    previewImage.decoding = 'async';

    const previewOverlay = document.createElement('div');
    previewOverlay.className = 'mt-classes-video-poster-overlay';
    previewOverlay.innerHTML = `
      <svg width="80" height="80" viewBox="0 0 80 80" fill="none" aria-hidden="true">
        <circle cx="40" cy="40" r="35" fill="rgba(255,255,255,0.2)"></circle>
        <polygon points="32,25 32,55 55,40" fill="white"></polygon>
      </svg>
    `;

    this.videoContainer.appendChild(previewImage);
    this.videoContainer.appendChild(previewOverlay);
  }

  loadInitialVideo() {
    // Load the first video from the first sport type (but don't auto-play)
    // Only for desktop
    if (this.isMobile || this.hasLoadedInitialVideo) return;

    const initialTarget = this.getInitialClassTarget();
    if (!initialTarget) return;

    this.hasLoadedInitialVideo = true;
    this.disconnectViewportObservers();
    this.selectClass(initialTarget.list, initialTarget.classItem, initialTarget.index);
  }

  loadVideoInModal(url, posterUrl = null) {
    if (!this.videoModalPlayer || !url) return;

    // Clear existing content
    this.videoModalPlayer.innerHTML = '';

    // Check video type and load
    if (this.isYouTubeUrl(url)) {
      this.loadYouTubeVideoInModal(url);
    } else if (this.isVimeoUrl(url)) {
      this.loadVimeoVideoInModal(url);
    } else {
      this.loadDirectVideoInModal(url, posterUrl);
    }

    // Show modal
    this.openVideoModal();
  }

  loadYouTubeVideoInModal(url) {
    let videoId = '';
    
    if (url.includes('youtu.be/')) {
      videoId = url.split('youtu.be/')[1].split('?')[0];
    } else if (url.includes('youtube.com/watch')) {
      const urlParams = new URLSearchParams(url.split('?')[1]);
      videoId = urlParams.get('v');
    } else if (url.includes('youtube.com/embed/')) {
      videoId = url.split('embed/')[1].split('?')[0];
    }

    if (videoId) {
      const iframe = document.createElement('iframe');
      iframe.src = `https://www.youtube.com/embed/${videoId}?controls=1&rel=0&enablejsapi=1`;
      iframe.allow = 'accelerometer; clipboard-write; encrypted-media; gyroscope; picture-in-picture';
      iframe.allowFullscreen = true;
      iframe.id = 'youtube-player-modal';
      this.videoModalPlayer.appendChild(iframe);
    }
  }

  loadVimeoVideoInModal(url) {
    let videoId = '';
    
    if (url.includes('vimeo.com/')) {
      videoId = url.split('vimeo.com/')[1].split('?')[0];
    }

    if (videoId) {
      const iframe = document.createElement('iframe');
      iframe.src = `https://player.vimeo.com/video/${videoId}?controls=1`;
      iframe.allow = 'fullscreen; picture-in-picture';
      iframe.allowFullscreen = true;
      iframe.id = 'vimeo-player-modal';
      this.videoModalPlayer.appendChild(iframe);
    }
  }

  loadDirectVideoInModal(url, posterUrl = null) {
    const video = document.createElement('video');
    video.src = url;
    video.controls = true;
    video.autoplay = false;
    // Set poster first for immediate cover display, then use metadata preload
    if (posterUrl) {
      video.poster = posterUrl;
      video.preload = 'metadata'; // With poster set, metadata is enough
    } else {
      video.preload = 'auto'; // Without poster, preload more for faster first frame
    }
    video.playsInline = true;
    video.id = 'direct-video-player-modal';
    
    const source = document.createElement('source');
    source.src = url;
    
    if (url.includes('.mp4')) {
      source.type = 'video/mp4';
    } else if (url.includes('.webm')) {
      source.type = 'video/webm';
    } else if (url.includes('.ogg')) {
      source.type = 'video/ogg';
    }
    
    video.appendChild(source);
    
    const fallback = document.createElement('p');
    fallback.textContent = 'Your browser does not support the video tag.';
    fallback.style.color = 'white';
    fallback.style.textAlign = 'center';
    fallback.style.padding = '20px';
    video.appendChild(fallback);
    
    this.videoModalPlayer.appendChild(video);
    
    // 仅使用浏览器原生控制条控制播放/暂停，不再额外绑定点击切换逻辑
    // Show play button overlay
    this.showPlayButtonInModal(video);
  }

  showPlayButtonInModal(video) {
    const playButton = document.createElement('div');
    playButton.className = 'video-play-overlay';
    // playButton.innerHTML = `
    //   <svg width="80" height="80" viewBox="0 0 80 80" fill="none">
    //     <circle cx="40" cy="40" r="35" fill="rgba(255,255,255,0.2)"></circle>
    //     <polygon points="32,25 32,55 55,40" fill="white"></polygon>
    //   </svg>
    // `;
    playButton.style.position = 'absolute';
    playButton.style.top = '50%';
    playButton.style.left = '50%';
    playButton.style.transform = 'translate(-50%, -50%)';
    playButton.style.cursor = 'pointer';
    playButton.style.zIndex = '2';
    
    // playButton.addEventListener('click', () => {
    //   video.play();
    //   playButton.remove();
    // });
    
    this.videoModalPlayer.appendChild(playButton);
  }

  openVideoModal() {
    if (this.videoModal) {
      this.videoModal.classList.add('active');
      document.body.style.overflow = 'hidden'; // Prevent background scrolling
    }
  }

  closeVideoModal() {
    if (this.videoModal) {
      this.videoModal.classList.remove('active');
      document.body.style.overflow = ''; // Restore scrolling
      
      // Pause any playing videos
      const video = this.videoModalPlayer.querySelector('video');
      if (video) {
        video.pause();
      }
      
      // Clear modal content after animation
      setTimeout(() => {
        if (!this.videoModal.classList.contains('active')) {
          this.videoModalPlayer.innerHTML = '';
        }
      }, 300);
    }
  }

  loadVideo(url, posterUrl = null) {
    if (!this.videoContainer || !url) return;

    // Clear existing content
    this.videoContainer.innerHTML = '';

    // Check video type
    if (this.isYouTubeUrl(url)) {
      this.loadYouTubeVideo(url);
    } else if (this.isVimeoUrl(url)) {
      this.loadVimeoVideo(url);
    } else {
      this.loadDirectVideo(url, posterUrl);
    }
  }

  isYouTubeUrl(url) {
    return url.includes('youtube.com') || url.includes('youtu.be');
  }

  isVimeoUrl(url) {
    return url.includes('vimeo.com');
  }

  loadYouTubeVideo(url) {
    let videoId = '';
    
    if (url.includes('youtu.be/')) {
      videoId = url.split('youtu.be/')[1].split('?')[0];
    } else if (url.includes('youtube.com/watch')) {
      const urlParams = new URLSearchParams(url.split('?')[1]);
      videoId = urlParams.get('v');
    } else if (url.includes('youtube.com/embed/')) {
      videoId = url.split('embed/')[1].split('?')[0];
    }

    if (videoId) {
      const iframe = document.createElement('iframe');
      // Remove autoplay=1 - videos should only play when user clicks
      iframe.src = `https://www.youtube.com/embed/${videoId}?controls=1&rel=0&enablejsapi=1`;
      iframe.allow = 'accelerometer; clipboard-write; encrypted-media; gyroscope; picture-in-picture';
      iframe.allowFullscreen = true;
      iframe.id = 'youtube-player';
      this.videoContainer.appendChild(iframe);
      
      // Add click overlay for basic pause/play control
      this.addVideoClickOverlay(iframe, 'youtube');
    }
  }

  loadVimeoVideo(url) {
    let videoId = '';
    
    if (url.includes('vimeo.com/')) {
      videoId = url.split('vimeo.com/')[1].split('?')[0];
    }

    if (videoId) {
      const iframe = document.createElement('iframe');
      // Remove autoplay=1 - videos should only play when user clicks
      iframe.src = `https://player.vimeo.com/video/${videoId}?controls=1`;
      iframe.allow = 'fullscreen; picture-in-picture';
      iframe.allowFullscreen = true;
      iframe.id = 'vimeo-player';
      this.videoContainer.appendChild(iframe);
      
      // Add click overlay for basic pause/play control
      this.addVideoClickOverlay(iframe, 'vimeo');
    }
  }

  loadDirectVideo(url, posterUrl = null) {
    const video = document.createElement('video');
    video.src = url;
    video.controls = true;
    // Remove autoplay - videos should only play when user clicks
    video.autoplay = false;
    // Set poster first for immediate cover display, then use metadata preload
    if (posterUrl) {
      video.poster = posterUrl;
      video.preload = 'metadata'; // With poster set, metadata is enough
    } else {
      video.preload = 'auto'; // Without poster, preload more for faster first frame
    }
    video.playsInline = true;
    video.style.width = '100%';
    video.style.height = '100%';
    video.style.objectFit = 'contain';
    video.style.backgroundColor = '#000';
    video.id = 'direct-video-player';
    
    // Add multiple source formats if available
    const source = document.createElement('source');
    source.src = url;
    
    // Detect video format from URL
    if (url.includes('.mp4')) {
      source.type = 'video/mp4';
    } else if (url.includes('.webm')) {
      source.type = 'video/webm';
    } else if (url.includes('.ogg')) {
      source.type = 'video/ogg';
    }
    
    video.appendChild(source);
    
    // Fallback message
    const fallback = document.createElement('p');
    fallback.textContent = 'Your browser does not support the video tag.';
    fallback.style.color = 'white';
    fallback.style.textAlign = 'center';
    fallback.style.padding = '20px';
    video.appendChild(fallback);
    
    this.videoContainer.appendChild(video);
    
    // 仅使用浏览器原生控制条控制播放/暂停，不再额外绑定点击切换逻辑
    // Show play button overlay - don't auto-play
    this.showPlayButton(video);
  }

  showPlayButton(video) {
    const playButton = document.createElement('div');
    playButton.className = 'video-play-overlay';
    playButton.innerHTML = `
      <svg width="80" height="80" viewBox="0 0 80 80" fill="none">
        <circle cx="40" cy="40" r="35" fill="rgba(255,255,255,0.2)"></circle>
        <polygon points="32,25 32,55 55,40" fill="white"></polygon>
      </svg>
    `;
    playButton.style.position = 'absolute';
    playButton.style.top = '50%';
    playButton.style.left = '50%';
    playButton.style.transform = 'translate(-50%, -50%)';
    playButton.style.cursor = 'pointer';
    playButton.style.zIndex = '2';
    
    playButton.addEventListener('click', () => {
      video.play();
      playButton.remove();
    });
    
    this.videoContainer.appendChild(playButton);
  }

  // Add click to pause/play for direct video
  addVideoClickControl(video) {
    video.addEventListener('click', (e) => {
      // Don't interfere with control bar clicks
      if (e.target === video) {
        if (video.paused) {
          video.play();
        } else {
          video.pause();
        }
      }
    });

    // Add keyboard support
    video.addEventListener('keydown', (e) => {
      if (e.code === 'Space' || e.code === 'Enter') {
        e.preventDefault();
        if (video.paused) {
          video.play();
        } else {
          video.pause();
        }
      }
    });

    // Make video focusable for keyboard navigation
    video.tabIndex = 0;
  }

  // Add click overlay for iframe videos (YouTube/Vimeo)
  addVideoClickOverlay(iframe, type) {
    // Create invisible overlay for click detection
    const overlay = document.createElement('div');
    overlay.style.position = 'absolute';
    overlay.style.top = '0';
    overlay.style.left = '0';
    overlay.style.width = '100%';
    overlay.style.height = '100%';
    overlay.style.backgroundColor = 'transparent';
    overlay.style.cursor = 'pointer';
    overlay.style.zIndex = '1';
    overlay.title = 'Click to pause/play video';
    
    // Track play state
    let isPlaying = true;
    
    overlay.addEventListener('click', () => {
      if (type === 'youtube') {
        // For YouTube, try to use postMessage API
        if (isPlaying) {
          iframe.contentWindow.postMessage('{"event":"command","func":"pauseVideo","args":""}', '*');
        } else {
          iframe.contentWindow.postMessage('{"event":"command","func":"playVideo","args":""}', '*');
        }
      } else if (type === 'vimeo') {
        // For Vimeo, use their player API
        if (isPlaying) {
          iframe.contentWindow.postMessage('{"method":"pause"}', '*');
        } else {
          iframe.contentWindow.postMessage('{"method":"play"}', '*');
        }
      }
      isPlaying = !isPlaying;
    });
    
    this.videoContainer.appendChild(overlay);
    
    // Hide overlay after a few seconds to not interfere with native controls
    setTimeout(() => {
      overlay.style.display = 'none';
    }, 5000);
    
    // Show overlay again on hover
    this.videoContainer.addEventListener('mouseenter', () => {
      overlay.style.display = 'block';
    });
    
    this.videoContainer.addEventListener('mouseleave', () => {
      setTimeout(() => {
        overlay.style.display = 'none';
      }, 2000);
    });
  }

  scrollTabs(direction) {
    if (!this.tabsContainer) return;

    const scrollAmount = 300;
    const currentScroll = this.tabsContainer.scrollLeft;

    if (direction === 'prev') {
      this.tabsContainer.scrollTo({
        left: currentScroll - scrollAmount,
        behavior: 'smooth'
      });
    } else {
      this.tabsContainer.scrollTo({
        left: currentScroll + scrollAmount,
        behavior: 'smooth'
      });
    }

    // Update button states after scroll
    setTimeout(() => {
      this.updateNavButtons();
    }, 300);
  }

  updateNavButtons() {
    if (!this.tabsContainer || !this.navPrev || !this.navNext) return;

    const isAtStart = this.tabsContainer.scrollLeft <= 0;
    const isAtEnd = this.tabsContainer.scrollLeft + this.tabsContainer.clientWidth >= this.tabsContainer.scrollWidth - 1;

    this.navPrev.disabled = isAtStart;
    this.navNext.disabled = isAtEnd;
  }
}

// Initialize all MT Classes components on the page
document.addEventListener('DOMContentLoaded', () => {
  const classesWrappers = document.querySelectorAll('.mt-classes-wrapper');
  classesWrappers.forEach((wrapper) => {
    new MTClasses(wrapper);
  });
});

// Re-initialize on Shopify section reload (for theme customizer)
if (typeof Shopify !== 'undefined' && Shopify.designMode) {
  document.addEventListener('shopify:section:load', (event) => {
    const wrapper = event.target.querySelector('.mt-classes-wrapper');
    if (wrapper) {
      new MTClasses(wrapper);
    }
  });
}
